  import { useState, useEffect } from 'react';
import { ChevronLeft, MapPin, Calendar, Truck, CheckCircle, Phone, CreditCard, Plus, User, Building2, Clock, Lock, AlertCircle } from 'lucide-react';

export default function ConfirmOrderPage({ orderData, bankCards, selectedCardId, onBack, onSubmit, onSelectBankCard, onAddBankCard, onReadContract, purchaseContractAgreed, serviceContractAgreed }) {
  const { base, material, weight, mode, supplierInfo, demandType, deliveryDeadline } = orderData || {};
  
  const [agreed, setAgreed] = useState(false);
  
  const isOpenDemand = demandType === 'open' || !demandType;
  const isClosedDemand = demandType === 'closed';
  const demandTypeLabel = isOpenDemand ? '敞口需求' : '闭口需求';
  const demandTypeColor = isOpenDemand ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600';
  const isSelfMode = mode === 'self';

  const estimatedAmount = (parseFloat(weight) * 2650).toFixed(2);

  useEffect(() => {
    if (isSelfMode) {
      if (purchaseContractAgreed && serviceContractAgreed) {
        setAgreed(true);
      } else {
        setAgreed(false);
      }
    }
  }, [purchaseContractAgreed, serviceContractAgreed, isSelfMode]);

  const canSubmit = () => {
    if (!selectedCardId) return false;
    if (!agreed) return false;
    return true;
  };

  const handleSubmit = () => {
    if (!canSubmit()) return;
    onSubmit();
  };

  return (
    <div className="bg-slate-50 min-h-full pb-24">
      <div className="sticky top-0 bg-white/95 backdrop-blur-sm z-10 px-4 py-3 border-b border-slate-200 flex items-center shadow-sm">
        <button onClick={onBack} className="p-1 -ml-2">
          <ChevronLeft size={24} className="text-slate-700" />
        </button>
        <span className="font-bold text-slate-800 ml-2 text-lg">确认订单</span>
      </div>

      <div className="px-4 py-4 space-y-4">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-4 text-white shadow-md">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle size={16} />
            <span className="text-sm font-medium">订单确认</span>
          </div>
          <div className="font-bold text-xl">请核对订单信息</div>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1 h-5 bg-gradient-to-b from-blue-400 to-blue-600 rounded-full" />
            <h3 className="font-bold text-slate-800">收货基地</h3>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-16 h-16 bg-slate-200 rounded-lg overflow-hidden shrink-0">
              <img src={base?.image || 'https://picsum.photos/seed/base1/400/300'} alt={base?.name} className="w-full h-full object-cover" />
            </div>
            <div className="flex-1">
              <div className="font-bold text-slate-800 text-base mb-1">{base?.name || '马钢诚兴马鞍山基地'}</div>
              <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">
                <MapPin size={12} />
                <span className="truncate">{base?.address || '安徽省马鞍山市雨山区'}</span>
              </div>
              <div className="flex items-center gap-1 text-xs text-slate-500">
                <Phone size={12} />
                <span>400-888-8888</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1 h-5 bg-gradient-to-b from-blue-400 to-blue-600 rounded-full" />
            <h3 className="font-bold text-slate-800">合同双方</h3>
          </div>

          <div className="space-y-4">
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                  <Building2 size={16} className="text-white" />
                </div>
                <div>
                  <div className="text-xs text-blue-600 font-medium">甲方（买方）</div>
                  <div className="text-sm font-bold text-blue-900">{base?.name || '马钢诚兴马鞍山基地'}</div>
                </div>
              </div>
              <div className="text-xs text-blue-800">
                地址：{base?.address || '安徽省马鞍山市雨山区'}
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                  <User size={16} className="text-white" />
                </div>
                <div>
                  <div className="text-xs text-green-600 font-medium">乙方（卖方）</div>
                  <div className="text-sm font-bold text-green-900">张三</div>
                </div>
              </div>
              <div className="space-y-1 text-xs text-green-800">
                <div>身份证号：340123199001011234</div>
                <div>联系电话：138****8888</div>
                <div>地址：安徽省马鞍山市花山区</div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1 h-5 bg-gradient-to-b from-blue-400 to-blue-600 rounded-full" />
            <h3 className="font-bold text-slate-800">物料信息</h3>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">物料名称</span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-slate-800">{material || '重型废钢 (HMS1)'}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${demandTypeColor}`}>
                  {demandTypeLabel}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">
                {isClosedDemand ? '固定重量' : '预估重量'}
              </span>
              <div className="flex items-center gap-1">
                {isClosedDemand && <Lock size={12} className="text-purple-500" />}
                <span className={`text-sm font-bold ${isClosedDemand ? 'text-purple-600' : 'text-slate-800'}`}>
                  {weight} 吨
                </span>
                {isClosedDemand && <span className="text-xs text-purple-500">（固定）</span>}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">参考单价</span>
              <span className="text-sm font-bold text-red-600">2,650.00 元/吨</span>
            </div>
            <div className="border-t border-slate-100 pt-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">预估金额</span>
                <span className="text-lg font-bold text-red-600">{estimatedAmount} 元</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1 h-5 bg-gradient-to-b from-blue-400 to-blue-600 rounded-full" />
            <h3 className="font-bold text-slate-800">送货信息</h3>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                <Clock size={20} className="text-blue-500" />
              </div>
              <div>
                <div className="text-xs text-slate-500">需求有效期</div>
                <div className="text-sm font-bold text-slate-800">
                  {isOpenDemand ? '半年内有效' : `截止 ${deliveryDeadline}`}
                </div>
                {isClosedDemand && (
                  <div className="text-xs text-purple-500 mt-0.5">闭口需求，请在截止日期前完成送货</div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                <Calendar size={20} className="text-blue-500" />
              </div>
              <div>
                <div className="text-xs text-slate-500">收货时间</div>
                <div className="text-sm font-bold text-slate-800">周一至周日 8:00-18:00</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                <Truck size={20} className="text-blue-500" />
              </div>
              <div>
                <div className="text-xs text-slate-500">送货方式</div>
                <div className="text-sm font-bold text-slate-800">自行送货到基地</div>
              </div>
            </div>
          </div>
        </div>

        {mode === 'other' && supplierInfo && (
          <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-200">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-1 h-5 bg-gradient-to-b from-orange-400 to-orange-600 rounded-full" />
              <h3 className="font-bold text-orange-900">实供人信息</h3>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-orange-700">姓名</span>
                <span className="text-sm font-bold text-orange-900">{supplierInfo.name}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-orange-700">手机号</span>
                <span className="text-sm font-bold text-orange-900">{supplierInfo.phone}</span>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <CreditCard size={18} className="text-blue-500" />
              <h3 className="font-bold text-slate-800">收款银行卡</h3>
            </div>
            <button
              onClick={onAddBankCard}
              className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 transition-colors"
            >
              <Plus size={14} />
              添加银行卡
            </button>
          </div>

          {selectedCardId && bankCards && bankCards.length > 0 ? (
            (() => {
              const selectedCard = bankCards.find(card => card.id === selectedCardId);
              if (!selectedCard) return null;
              
              const getBankColor = (bankName) => {
                const colors = {
                  '工商银行': 'from-red-500 to-red-600',
                  '建设银行': 'from-blue-500 to-blue-600',
                  '农业银行': 'from-green-500 to-green-600',
                  '中国银行': 'from-red-600 to-red-700',
                  '招商银行': 'from-red-400 to-red-500',
                  '交通银行': 'from-blue-600 to-blue-700',
                  '浦发银行': 'from-blue-400 to-blue-500',
                  '中信银行': 'from-red-500 to-red-600',
                  '光大银行': 'from-orange-500 to-orange-600',
                  '民生银行': 'from-blue-500 to-blue-600',
                  '平安银行': 'from-orange-400 to-orange-500',
                  '华夏银行': 'from-red-500 to-red-600',
                };
                return colors[bankName] || 'from-slate-500 to-slate-600';
              };

              const getBankIcon = (bankName) => {
                return bankName?.charAt(0) || '银';
              };

              return (
                <div
                  onClick={onSelectBankCard}
                  className="relative overflow-hidden rounded-xl cursor-pointer transition-all"
                >
                  <div className={`bg-gradient-to-r ${getBankColor(selectedCard.bankName)} p-4 text-white`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center font-bold">
                          {getBankIcon(selectedCard.bankName)}
                        </div>
                        <div>
                          <div className="font-bold text-lg">{selectedCard.bankName}</div>
                          <div className="text-xs text-white/80">储蓄卡</div>
                        </div>
                      </div>
                      <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
                        <CheckCircle size={14} className="text-green-500" />
                      </div>
                    </div>
                    <div className="font-mono text-lg tracking-wider mb-2">
                      **** **** **** {selectedCard.cardNumber.slice(-4)}
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <div>
                        <div className="text-white/60 text-xs">开户支行</div>
                        <div className="font-medium text-xs">{selectedCard.branch || '-'}</div>
                      </div>
                      <div>
                        <div className="text-white/60 text-xs">有效期</div>
                        <div className="font-medium">{selectedCard.expiryDate}</div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-blue-50 px-4 py-2 text-center border-t border-blue-100">
                    <span className="text-xs text-blue-600">点击更换银行卡</span>
                  </div>
                </div>
              );
            })()
          ) : (
            <div
              onClick={onAddBankCard}
              className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all"
            >
              <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CreditCard size={24} className="text-slate-400" />
              </div>
              <div className="text-sm text-slate-600 font-medium mb-1">添加收款银行卡</div>
              <div className="text-xs text-slate-400">点击添加银行卡</div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <div className="flex items-start gap-3">
            <div
              onClick={() => {
                if (!agreed && purchaseContractAgreed && serviceContractAgreed) {
                  setAgreed(true);
                } else if (agreed) {
                  setAgreed(false);
                } else if (!purchaseContractAgreed || !serviceContractAgreed) {
                  onReadContract('self');
                }
              }}
              className={`mt-0.5 w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition-all cursor-pointer ${
                agreed
                  ? 'bg-blue-500 border-blue-500'
                  : purchaseContractAgreed && serviceContractAgreed
                  ? 'border-slate-300 bg-white hover:border-blue-400'
                  : 'border-slate-300 bg-white hover:border-blue-400'
              }`}
            >
              {agreed && <CheckCircle size={14} className="text-white" />}
            </div>
            <div className="flex-1">
              <div className="text-sm text-slate-600 leading-relaxed">
                {purchaseContractAgreed && serviceContractAgreed ? (
                  <>
                    我已阅读并同意 
                    <span className="font-medium text-green-600">
                      《废钢采购合同》✓
                    </span>
                    、
                    <span className="font-medium text-green-600">
                      《平台服务协议》✓
                    </span>
                  </>
                ) : (
                  <>
                    我已阅读并同意 
                    <span className="font-medium text-blue-500 cursor-pointer" onClick={() => onReadContract('self')}>
                      《废钢采购合同》
                    </span>
                    、
                    <span className="font-medium text-blue-500 cursor-pointer" onClick={() => onReadContract('self')}>
                      《平台服务协议》
                    </span>
                  </>
                )}
              </div>
              <p className="mt-1 text-slate-400 text-xs">勾选即代表签署电子合同，系统将自动生成具有法律效力的电子文本。</p>
              {(!purchaseContractAgreed || !serviceContractAgreed) && (
                <div className="mt-2 flex items-center gap-2 text-orange-600 text-xs font-medium">
                  <AlertCircle size={12} />
                  <span>请先阅读并同意采购合同和平台服务协议</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="fixed bottom-16 left-0 right-0 z-20 flex justify-center pointer-events-none">
        <div className="w-full max-w-md bg-white border-t border-slate-200 p-4 pointer-events-auto">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-slate-500">预估金额</div>
            <div className="text-2xl font-bold text-red-600">{estimatedAmount} <span className="text-sm font-normal text-slate-500">元</span></div>
          </div>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit()}
            className={`w-full py-3.5 rounded-xl font-bold text-base shadow-lg transition-all ${
              canSubmit()
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:shadow-xl active:scale-[0.98]'
                : 'bg-slate-300 text-slate-500 cursor-not-allowed'
            }`}
          >
            确认提交订单
          </button>
        </div>
      </div>
    </div>
  );
}
